import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  IAssessmentResource,
  ILineResource,
  IMaintenanceResource,
  ISeedManage,
} from '../../entities/farms.entities';
import {
  IWeatherConditionResource,
  TBusinessType,
} from '../../entities/general.entities';
import { ModalComponent, Subtitle, Title } from '../shared';
import { Modal, Table, Tabs } from 'antd';
import MaintenanceModal from '../farm-modals/MaintenanceModal';
import AssessmentModal from '../farm-modals/AssessmentModal';
import WeatherInfoModal from '../farm-modals/WeatherInfoModal';
import FloatManageModal from '../farm-modals/FloatManageModal';
import weather_icon from '../../images/weather-icon.png';
import { sendSingleRequest } from '../../apis';
import { showFeedback } from '../../store/ui/ui.actions';
import { selectLang } from '../../store/ui/ui.selector';
import { translate } from '../../lib/lang.helper';
import { TLang } from '../../entities/ui.entities';
import { SelectAssessFields } from '../../store/extra/extra.selector';
import { IInventoryManage } from '../../entities/inventory.entities';
import { getLineGrowingData, inventoryLabel } from '../../lib/farm.helpers';
import { formatNumber, hashColor } from '../../entities/util-functions';
import {
  defaultDateFormat,
  directionChar,
  displayValueUnit,
  getAverageSize,
  getUnitOfSpecie,
} from '../../lib/common.helpers';
import DotsMenu from '../shared/dropdown-menu/DotsMenu';
import { selectBoats } from '../../store/users/users.selector';
import {
  selectAccount,
  selectIsAdmin,
  selectProfile,
} from '../../store/auth/auth.selector';
import FilesViewModal from '../shared/pictures-wall/FilesViewModal';

const typeLabel: any = {
  VISUAL_ASSESSMENT: 'Visual Assessment',
  LINE_CLEANING: 'Line Cleaning',
  GENERAL_MAINTENANCE: 'General Maintenance',
};

const CustomFieldsViewModal: React.FC<{
  visible: boolean;
  onClose: () => void;
  data: { label: string; value: string | null }[];
  lang: TLang;
}> = ({ visible, lang, onClose, data }) => (
  <Modal
    title={translate(lang, 'Additional Data')}
    visible={visible}
    onCancel={onClose}
    footer={null}
    width={400}
    closable
  >
    <div className='wrap'>
      {data.map((item, index) => (
        <div key={index} className='mb-17'>
          <Subtitle size={3} color='black-2' align='left' fontWeight={400}>
            {item.label}
          </Subtitle>
          <Subtitle size={5} color='black-5' align='left' fontWeight={400}>
            {item.value ?? '-'}
          </Subtitle>
        </div>
      ))}
    </div>
  </Modal>
);

interface Props {
  type: TBusinessType;
  assessments?: IAssessmentResource[];
  maintenances?: IMaintenanceResource[];
  floats?: IInventoryManage[];
  seed_manages?: ISeedManage[];
  loadLine?: () => void;
  lineData?: ILineResource;
  withTitle?: boolean;
}

const MaintenanceTable = ({
  type: businessType,
  assessments,
  maintenances,
  floats,
  seed_manages,
  loadLine,
  lineData,
  withTitle,
}: Props) => {
  const dispatch = useDispatch<any>();
  const lang = useSelector(selectLang);
  const customFields = SelectAssessFields(lineData?.farm_id);
  const paramDensityUnit =
    lineData && getLineGrowingData(lineData).density_unit === 'kgs per m'
      ? 'kg_per_m'
      : 'spat_mer_m';
  const unitType = useSelector(selectAccount)?.display_unit;
  const profile = useSelector(selectProfile);
  const isAdmin = useSelector(selectIsAdmin);
  const boats = useSelector(selectBoats);

  const [comment, setComment] = useState<string>();
  const [imgPdfFiles, setImgPdfFiles] = useState<string[]>();
  const [mntData, setMntData] = useState<IMaintenanceResource>();
  const [assData, setAssData] = useState<IAssessmentResource>();
  const [fltData, setFltData] = useState<IInventoryManage>();
  const [weather, setWeather] = useState<IWeatherConditionResource>();
  const [viewMoreData, setViewMoreData] =
    useState<{ label: string; value: string | null }[]>();

  const openImages = (is: string[]) => setImgPdfFiles(is);

  const deleteAssessment = async (d: IAssessmentResource) => {
    if (!window.confirm(translate(lang, '_delete_confirm', 'assessment')))
      return;
    const response = await sendSingleRequest(
      {},
      'DELETE',
      `api/farm/line/assessment/assessments/${d.id}`,
      true,
    );
    if (response.status) {
      if (loadLine) {
        await loadLine();
      }
      dispatch(
        showFeedback({
          isMessage: true,
          type: 'success',
          message: translate(lang, '_deleted_ok', 'Assessment'),
        }),
      );
    } else {
      dispatch(
        showFeedback({
          isMessage: true,
          type: 'error',
          message: translate(
            lang,
            response.data?.message ?? 'Failed to delete %s',
            'assessment',
          ),
        }),
      );
    }
  };
  const deleteMaintenance = async (d: IMaintenanceResource) => {
    if (!window.confirm(translate(lang, '_delete_confirm', 'maintenance')))
      return;
    const response = await sendSingleRequest(
      {},
      'DELETE',
      `api/farm/line/seed/maintenance/${d.id}`,
      true,
    );
    if (response.status) {
      if (loadLine) {
        await loadLine();
      }
      dispatch(
        showFeedback({
          isMessage: true,
          type: 'success',
          message: translate(lang, '_deleted_ok', 'Maintenance'),
        }),
      );
    } else {
      dispatch(
        showFeedback({
          isMessage: true,
          type: 'error',
          message: translate(
            lang,
            response.data?.message ?? 'Failed to delete %s',
            'maintenance',
          ),
        }),
      );
    }
  };
  const deleteFloating = async (d: IInventoryManage) => {
    if (!window.confirm(translate(lang, '_delete_confirm', 'floating'))) return;
    const response = await sendSingleRequest(
      {},
      'DELETE',
      `api/inventory/inventory-seed/${d.id}`,
      true,
    );
    if (response.status) {
      if (loadLine) {
        await loadLine();
      }
      dispatch(
        showFeedback({
          isMessage: true,
          type: 'success',
          message: translate(lang, '_deleted_ok', 'Floating'),
        }),
      );
    } else {
      dispatch(
        showFeedback({
          isMessage: true,
          type: 'error',
          message: translate(
            lang,
            response.data?.message ?? 'Failed to delete %s',
            'floating',
          ),
        }),
      );
    }
  };

  const sortedAssessments = useMemo(() => {
    return assessments
      ? [...assessments].sort((a, b) =>
          a.assessment_date > b.assessment_date ? -1 : 1,
        )
      : [];
  }, [assessments]);

  const sortedMaintenances = useMemo(() => {
    return maintenances
      ? [...maintenances].sort((a, b) =>
          a.maintain_date > b.maintain_date ? -1 : 1,
        )
      : [];
  }, [maintenances]);

  const sortedFloats = useMemo(() => {
    return floats
      ? [...floats].sort((a, b) => (a.manage_date > b.manage_date ? -1 : 1))
      : [];
  }, [floats]);

  const sortedSeedManages = useMemo(() => {
    return seed_manages
      ? [...seed_manages].sort((a, b) =>
          a.manage_date > b.manage_date ? -1 : 1,
        )
      : [];
  }, [seed_manages]);

  const [activeTab, setActiveTab] = useState<string>('assessments');

  const sizeUnit = getUnitOfSpecie(unitType, 'size_mm');
  const densityUnit = getUnitOfSpecie(unitType, paramDensityUnit);

  const assessmentColumns = useMemo(() => {
    const columns: any[] = [
      {
        title: translate(lang, 'Date of assessment'),
        key: 'assessment_date',
        render: (item: IAssessmentResource) => (
          <>
            <span>{defaultDateFormat(item.assessment_date)}</span>
            <br /> <span>{item.user?.name ?? translate(lang, 'Unknown')}</span>
            {item.verifier && (
              <>
                <br />
                <span className='verifier-name'>
                  {translate(lang, 'Verified by %s', item.verifier.name)}
                </span>
              </>
            )}
          </>
        ),
      },
      {
        title: `${translate(lang, 'Size min')} (${sizeUnit})`,
        key: 'size_min',
        render: (item: IAssessmentResource) => item.shell_size.min,
      },
      {
        title: `${translate(lang, 'Size max')} (${sizeUnit})`,
        key: 'size_max',
        render: (item: IAssessmentResource) => item.shell_size.max,
      },
      {
        title: `${translate(lang, 'Size avg')} (${sizeUnit})`,
        key: 'size_avg',
        render: (item: IAssessmentResource) => item.shell_size.avg,
      },
      {
        title: (
          <>
            <div>{translate(lang, 'Density')}</div>
            {!!densityUnit && <div>{`(${densityUnit})`}</div>}
          </>
        ),
        key: 'density',
        render: (item: IAssessmentResource) =>
          !item.density ? '-' : getAverageSize(item.density!, item.density_max),
      },
      {
        title: translate(lang, 'Planned harvest date'),
        key: 'planned_date_harvest',
        render: (item: IAssessmentResource) =>
          defaultDateFormat(item.planned_date_harvest),
      },
      {
        title: translate(
          lang,
          businessType === 'MUSSEL'
            ? 'ETA Tonnes'
            : businessType === 'OYSTER'
              ? 'ETA Dozens'
              : '',
        ),
        key: 'estimated_amount',
        render: (item: IAssessmentResource) =>
          item.estimated_amount
            ? businessType === 'OYSTER'
              ? formatNumber(item.estimated_amount)
              : displayValueUnit(
                  item.estimated_amount / 1000,
                  unitType,
                  'weight_t',
                )
            : '',
      },
      {
        title: translate(lang, 'Ready to harvest'),
        key: 'ready_harvest',
        render: (item: IAssessmentResource) =>
          defaultDateFormat(item.ready_harvest, '-'),
      },
      {
        title: translate(lang, 'Comment'),
        key: 'comment',
        render: (item: IAssessmentResource) =>
          !!item.comment && (
            <div
              className='btn__modal'
              onKeyDown={() => undefined}
              onClick={setComment.bind(this, item.comment)}
            >
              {translate(lang, 'View')}
            </div>
          ),
      },
      {
        title: translate(lang, 'Photo'),
        key: 'photo',
        render: (item: IAssessmentResource) =>
          item.image_pdf_files && item.image_pdf_files.length > 0 ? (
            <div
              className='btn__modal'
              onKeyDown={() => undefined}
              onClick={openImages.bind(this, item.image_pdf_files)}
            >
              {translate(lang, 'View')}
            </div>
          ) : (
            <span></span>
          ),
      },
      {
        title: translate(lang, 'Weather'),
        key: 'weather',
        render: (item: IAssessmentResource) =>
          item.weather_condition ? (
            <div
              className='btn__modal'
              style={{ display: 'flex' }}
              onClick={setWeather.bind(this, item.weather_condition)}
            >
              <img
                className='ant-image-img'
                src={weather_icon}
                alt='weather'
                style={{ maxWidth: '20px' }}
              />
              <span>{translate(lang, 'View')}</span>
            </div>
          ) : (
            <span></span>
          ),
      },
      {
        title: translate(lang, 'Boat'),
        key: 'boat',
        render: (item: IAssessmentResource) => {
          const boat = boats.find(x => x.id === item.boat_id);
          return (
            !!boat && (
              <span style={{ color: hashColor(boat.reg_number) }}>
                {boat.name}
              </span>
            )
          );
        },
      },
    ];
    if (lineData) {
      columns.push({
        title: '',
        key: 'actions',
        align: 'right',
        render: (item: IAssessmentResource) => (
          <DotsMenu
            items={[
              {
                label: translate(lang, 'View More Data'),
                onClick: () => {
                  const data = [
                    {
                      label: translate(lang, 'Direction'),
                      value: directionChar(item.direction, '-'),
                    },
                    ...customFields.map(field => ({
                      label: `${field.name} ${
                        field.unit ? `(${field.unit})` : ''
                      }`,
                      value:
                        item.custom_values?.find(v => v.field_id === field.id)
                          ?.field_val ?? '-',
                    })),
                  ];
                  setViewMoreData(data);
                },
              },
              {
                label: translate(lang, 'Edit'),
                onClick: () => setAssData(item),
              },
              ...(isAdmin
                ? [
                    {
                      label: translate(lang, 'Delete'),
                      onClick: () => deleteAssessment(item),
                      disabled: !isAdmin,
                    },
                  ]
                : []),
            ]}
          />
        ),
      });
    }

    return columns;
  }, [
    lang,
    sizeUnit,
    densityUnit,
    businessType,
    unitType,
    lineData,
    customFields,
    profile,
    boats,
    paramDensityUnit,
  ]);

  const maintenanceColumns = useMemo(() => {
    const columns: any[] = [
      {
        title: translate(lang, 'Date'),
        key: 'maintain_date',
        render: (item: IMaintenanceResource) => (
          <>
            <span>{defaultDateFormat(item.maintain_date)}</span>
            <br /> <span>{item.user?.name ?? translate(lang, 'Unknown')}</span>
            {item.verifier && (
              <>
                <br />
                <span className='verifier-name'>
                  {translate(lang, 'Verified by %s', item.verifier.name)}
                </span>
              </>
            )}
          </>
        ),
      },
      {
        title: translate(lang, 'Type'),
        key: 'type',
        render: (item: IMaintenanceResource) => typeLabel[item.type],
      },
      {
        title: translate(lang, 'Comment'),
        key: 'comment',
        render: (item: IMaintenanceResource) =>
          item.comment ?? translate(lang, 'No commented yet'),
      },
      {
        title: translate(lang, 'Photo'),
        key: 'photo',
        render: (item: IMaintenanceResource) =>
          item.image_pdf_files && item.image_pdf_files.length > 0 ? (
            <div
              className='btn__modal'
              onKeyDown={() => undefined}
              onClick={openImages.bind(this, item.image_pdf_files)}
            >
              {translate(lang, 'View')}
            </div>
          ) : (
            <span></span>
          ),
      },
      {
        title: translate(lang, 'Weather'),
        key: 'weather',
        render: (item: IMaintenanceResource) =>
          item.weather_condition ? (
            <div
              className='btn__modal'
              style={{ display: 'flex' }}
              onClick={setWeather.bind(this, item.weather_condition)}
            >
              <img
                className='ant-image-img'
                src={weather_icon}
                alt='weather'
                style={{ maxWidth: '20px' }}
              />
              <span>{translate(lang, 'View')}</span>
            </div>
          ) : (
            <span></span>
          ),
      },
      {
        title: translate(lang, 'Boat'),
        key: 'boat',
        render: (item: IMaintenanceResource) => {
          const boat = boats.find(x => x.id === item.boat_id);
          return (
            !!boat && (
              <span style={{ color: hashColor(boat.reg_number) }}>
                {boat.name}
              </span>
            )
          );
        },
      },
    ];

    if (lineData) {
      columns.push({
        title: '',
        key: 'actions',
        align: 'right',
        render: (item: IMaintenanceResource) => (
          <DotsMenu
            items={[
              {
                label: translate(lang, 'Edit'),
                onClick: () => setMntData(item),
              },
              ...(isAdmin
                ? [
                    {
                      label: translate(lang, 'Delete'),
                      onClick: () => deleteMaintenance(item),
                      disabled: !isAdmin,
                    },
                  ]
                : []),
            ]}
          />
        ),
      });
    }

    return columns;
  }, [lang, lineData, profile, boats]);

  const inventoryColumns = useMemo(() => {
    const columns: any[] = [
      {
        title: translate(lang, 'Date'),
        key: 'manage_date',
        render: (item: IInventoryManage) => (
          <>
            <span>{defaultDateFormat(item.manage_date)}</span>
            <br /> <span>{item.user?.name ?? translate(lang, 'Unknown')}</span>
            {item.verifier && (
              <>
                <br />
                <span className='verifier-name'>
                  {translate(lang, 'Verified by %s', item.verifier.name)}
                </span>
              </>
            )}
          </>
        ),
      },
      {
        title: translate(lang, 'Type'),
        key: 'type',
        render: () => translate(lang, 'Inventory management'),
      },
      {
        title: translate(lang, 'Details'),
        key: 'details',
        render: (item: IInventoryManage) =>
          item.status === 'IN'
            ? translate(
                lang,
                '_deducted_returned_storage',
                `${item.quantity} ${inventoryLabel(item.inventory)}`,
              )
            : translate(
                lang,
                '%s added',
                `${item.quantity} ${inventoryLabel(item.inventory)}`,
              ),
      },
      {
        title: translate(lang, 'Weather'),
        key: 'weather',
        render: (item: IInventoryManage) =>
          item.weather_condition ? (
            <div
              className='btn__modal'
              style={{ display: 'flex' }}
              onClick={setWeather.bind(this, item.weather_condition)}
            >
              <img
                className='ant-image-img'
                src={weather_icon}
                alt='weather'
                style={{ maxWidth: '20px' }}
              />
              <span>{translate(lang, 'View')}</span>
            </div>
          ) : (
            <span></span>
          ),
      },
      {
        title: translate(lang, 'Boat'),
        key: 'boat',
        render: (item: IInventoryManage) => {
          const boat = boats.find(x => x.id === item.boat_id);
          return (
            !!boat && (
              <span style={{ color: hashColor(boat.reg_number) }}>
                {boat.name}
              </span>
            )
          );
        },
      },
    ];

    if (lineData) {
      columns.push({
        title: '',
        key: 'actions',
        align: 'right',
        render: (item: IInventoryManage) => (
          <DotsMenu
            items={[
              {
                label: translate(lang, 'Edit'),
                onClick: () => setFltData(item),
              },
              ...(isAdmin
                ? [
                    {
                      label: translate(lang, 'Delete'),
                      onClick: () => deleteFloating(item),
                      disabled: !isAdmin,
                    },
                  ]
                : []),
            ]}
          />
        ),
      });
    }

    return columns;
  }, [lang, lineData, profile, boats]);

  const seedManageColumns = useMemo(
    () => [
      {
        title: translate(lang, 'Date'),
        key: 'manage_date',
        render: (item: ISeedManage) => (
          <span>{defaultDateFormat(item.manage_date)}</span>
        ),
      },
      {
        title: translate(lang, 'Type'),
        key: 'type',
        render: () => translate(lang, 'Line depth history'),
      },
      {
        title: translate(lang, 'Depth'),
        key: 'depth',
        render: (item: ISeedManage) =>
          displayValueUnit(item.submersion, unitType, 'distance_m'),
      },
    ],
    [lang, unitType],
  );

  return (
    <div style={{ overflow: 'auto' }}>
      {withTitle && (
        <Title
          className='ml-7 mb-17'
          size={6}
          color='black-3'
          align='default'
          fontWeight={700}
        >
          {translate(lang, 'Maintenances')}
        </Title>
      )}

      <Tabs type='line' activeKey={activeTab} onChange={setActiveTab}>
        <Tabs.TabPane key='assessments' tab={translate(lang, 'Assessments')}>
          <Table
            className='table table--isLine'
            rowKey={'id'}
            pagination={false}
            columns={assessmentColumns}
            dataSource={sortedAssessments}
            scroll={{ x: 1200 }}
          />
        </Tabs.TabPane>
        <Tabs.TabPane key='maintenances' tab={translate(lang, 'Maintenance')}>
          <Table
            className='table table--isLine'
            rowKey={'id'}
            pagination={false}
            columns={maintenanceColumns}
            dataSource={sortedMaintenances}
            scroll={{ x: 1200 }}
          />
        </Tabs.TabPane>
        <Tabs.TabPane
          key='inventory'
          tab={translate(lang, 'Inventory Management')}
        >
          <Table
            className='table table--isLine'
            rowKey={'id'}
            pagination={false}
            columns={inventoryColumns}
            dataSource={sortedFloats}
            scroll={{ x: 1200 }}
          />
        </Tabs.TabPane>
        <Tabs.TabPane key='seed' tab={translate(lang, 'Line Depth History')}>
          <Table
            className='table table--isLine'
            rowKey={'id'}
            pagination={false}
            columns={seedManageColumns}
            dataSource={sortedSeedManages}
            scroll={{ x: 1200 }}
          />
        </Tabs.TabPane>
      </Tabs>

      <ModalComponent
        title={translate(lang, 'Comment')}
        text={translate(lang, comment ?? '')}
        visible={!!comment}
        onCancel={() => setComment(undefined)}
        type=''
      />
      {imgPdfFiles !== undefined && (
        <FilesViewModal
          visible={true}
          onClose={() => setImgPdfFiles(undefined)}
          files={imgPdfFiles}
        />
      )}
      {!!mntData && lineData && (
        <MaintenanceModal
          visible={true}
          title={'_update_mnt_data'}
          onCancel={() => setMntData(undefined)}
          onConfirm={() => {
            setMntData(undefined);
            loadLine && loadLine();
          }}
          data={mntData}
          updateId={mntData.id}
          lineData={lineData}
        />
      )}
      {!!assData && lineData && (
        <AssessmentModal
          visible={true}
          title='Edit assessment'
          data={assData}
          onCancel={() => setAssData(undefined)}
          onConfirm={() => {
            setAssData(undefined);
            loadLine && loadLine();
          }}
          lineData={lineData}
          updateID={assData.id}
        />
      )}
      {!!fltData && lineData && (
        <FloatManageModal
          visible={true}
          title='Update floating'
          data={{ ...fltData, inventory_id: fltData.inventory.id }}
          onCancel={() => setFltData(undefined)}
          lineData={lineData}
          onConfirm={() => {
            setFltData(undefined);
            loadLine && loadLine();
          }}
          updateId={fltData.id}
        />
      )}
      {!!weather && (
        <WeatherInfoModal
          data={weather}
          visible={true}
          title='Weather Condition'
          onClose={() => setWeather(undefined)}
        />
      )}
      {viewMoreData !== undefined && (
        <CustomFieldsViewModal
          visible={true}
          onClose={() => setViewMoreData(undefined)}
          data={viewMoreData}
          lang={lang ?? 'en'}
        />
      )}
    </div>
  );
};

export default MaintenanceTable;
